/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import React, { useEffect, useRef } from 'react';
import { VStack } from 'chakra-v3';
import type { PlatformVideoComponent } from '@karya/core';
import type { ComponentRendererProps } from '../../shared/Types';
import { getTypedParams } from '../../shared/Types';
import { Text } from '../../../../../components/Text';
import { PlatformMediaErrors, PlatformFileErrors } from './ErrorMessages';
import { useNext } from '../../../hooks/useNext';
import { useErrorState } from '../../shared/useErrorState';
import { ChatBubble } from '../../shared/ChatBubble';

export const PlatformVideo: React.FC<ComponentRendererProps> = ({ node, controller, isCurrent }) => {
  const params = getTypedParams<PlatformVideoComponent>(node);
  const video = params.video ?? '';
  const [error, setError] = useErrorState();
  const videoRef = useRef<HTMLVideoElement>(null);

  // Set missing file error if video is not provided
  useEffect(() => {
    if (!video) {
      const missingFileError = PlatformFileErrors.getMissingFileError(video, 'video');
      setError(() => missingFileError);
    } else {
      setError(null);
    }
  }, [video, setError]);

  // Handle next component navigation
  // We use MANUAL strategy here because we want to advance only when the video ends
  const { next } = useNext({
    strategy: 'MANUAL',
    controller,
    isCurrent,
  });

  useEffect(() => {
    // Auto-advance after video ends (event-based)
    const videoElement = videoRef.current;
    if (videoElement) {
      const handleEnded = () => {
        next();
      };
      videoElement.addEventListener('ended', handleEnded);
      return () => {
        videoElement.removeEventListener('ended', handleEnded);
      };
    }
  }, [video, next]);

  const handleVideoError = (event: React.SyntheticEvent<HTMLVideoElement, Event>) => {
    const error = PlatformMediaErrors.getMediaElementError(event.nativeEvent, 'video', video || undefined);
    setError(() => error);
  };

  return (
    <ChatBubble>
      <VStack gap="12px" align="stretch">
        {error && (
          <Text variant="body" color="error.solid">
            {error.message}
          </Text>
        )}
        {video && (
          <video
            ref={videoRef}
            src={video}
            controls
            onError={handleVideoError}
            width="100%"
            style={{ maxHeight: '500px', borderRadius: '4px' }}
          />
        )}
      </VStack>
    </ChatBubble>
  );
};
